/**
 * Business Profiles — a paid, admin-approved profile type attached to a user's
 * existing account.
 *
 * Three documents, deliberately separate:
 *
 *   business_registrations   the pitch. Written before any money changes hands,
 *                            reviewed by a Watcher/Legion, then paid for.
 *   business_profiles        the live profile. Only exists after approval AND
 *                            payment, and is built from scratch — the pitch text
 *                            never becomes profile copy.
 *   business_owner_state     the two per-owner limits: the "you may not apply
 *                            again yet" cooldown, and how many profiles this
 *                            account currently owns.
 *
 * business_owner_state is a separate collection keyed by uid rather than fields
 * on the user document, for the same reason ban_appeals uses uid as its document
 * id: a Firestore rule cannot query a collection, so a limit is only enforceable
 * when the document holding it is addressable from request.auth.uid. It is
 * written exclusively by admins and Cloud Functions — a cooldown the applicant
 * can clear, or a profile counter they can zero, is decorative.
 */

// ── Tunables ─────────────────────────────────────────────────────────────────

/** Products a single business profile may list. Confirmed 2026-09-08. */
export const STORE_ITEM_CAP = 15;

/**
 * Days after a denial or a self-deletion before that owner may register again.
 * Confirmed 2026-09-08. (The ban-appeal cooldown next door is 30 — these are
 * deliberately different numbers for deliberately different offences.)
 */
export const BUSINESS_COOLDOWN_DAYS = 7;

/**
 * One confirmed-purchase notification per user, per business, per this window.
 * Over the limit the purchase still records as confirmed — the data point is
 * real — the owner just isn't pinged again. This is what stops someone
 * spam-tapping "yes" to harass a business. Confirmed 2026-09-08.
 */
export const PURCHASE_NOTIFY_COOLDOWN_HOURS = 24;

/**
 * Business profiles one account may own at once. Confirmed 2026-09-09.
 *
 * Counted against ACTIVE profiles plus registrations still in flight (pending
 * review, or approved and not yet built), so someone cannot queue up a fourth by
 * applying three times before any of them lands.
 */
export const MAX_BUSINESS_PROFILES = 3;

/** Portfolio is exactly this many images. Not "up to" — the builder requires all four. */
export const PORTFOLIO_IMAGE_COUNT = 4;

/** One-time registration fee, in whole US dollars. Mirrored in functions/business.js. */
export const BUSINESS_FEE_USD = 20;

/** Customer-base keywords one application may carry, and how long each may be. */
export const CUSTOMER_BASE_MAX_KEYWORDS = 10;
export const CUSTOMER_BASE_KEYWORD_MAX_LENGTH = 40;

export const PITCH_MAX_LENGTH = 1500;
export const BUSINESS_NAME_MAX_LENGTH = 60;
export const BUSINESS_DESCRIPTION_MAX_LENGTH = 500;
export const FEED_POST_MAX_LENGTH = 1000;

/** The URL param the tracking code rides in. Invisible to the user in practice. */
export const TRACKING_PARAM = 'ay_ref';

// ── Registration ─────────────────────────────────────────────────────────────

export type RegistrationStatus = 'pending' | 'approved' | 'denied';

export interface BusinessRegistration {
  id: string;
  ownerId: string;
  /**
   * What the business is called. Collected here rather than only in the builder
   * so a moderator is reviewing a named business rather than an anonymous
   * paragraph, and so nobody types their own business name twice — the builder
   * prefills from this.
   */
  businessName: string;
  /** What the business is, and what they want to do on the platform. */
  pitchText: string;
  /**
   * Who they sell to, as keywords. Stored as an array rather than the raw
   * comma-separated string the applicant typed, so it can be rendered as chips
   * and matched against later without every reader re-parsing it.
   */
  customerBase?: string[];
  shopLink: string;
  /**
   * Optional. A social profile the moderator can look at to see the business is
   * real — the single most useful vetting signal after the shop link itself.
   * Lives on the application only; it is not published on the live profile.
   */
  socialLink?: string | null;
  status: RegistrationStatus;
  submittedAt?: any;
  reviewedAt?: any;
  reviewedBy?: string | null;
  denialReason?: string | null;
  deniedAt?: any;
  cooldownExpiresAt?: any;
  /** Set by the Stripe webhook once payment succeeds. Never client-written. */
  paidAt?: any;
  stripeSessionId?: string | null;
  /** Set once the profile is built. Its presence is what closes the builder. */
  businessProfileId?: string | null;
  /** Denormalized for the review queue so it doesn't fan out to /users. */
  ownerName?: string;
  ownerUsername?: string;
}

// ── Live profile ─────────────────────────────────────────────────────────────

export type BusinessProfileStatus = 'active' | 'suspended';

/**
 * A name/link change an admin has yet to approve. The profile stays live and
 * keeps serving its OLD name and link while this sits here — a re-review is not
 * a takedown, and taking the profile offline for a typo fix would punish the
 * owner for using the feature correctly.
 */
export interface PendingBusinessEdit {
  businessName?: string;
  shopLink?: string;
  requestedAt?: any;
}

export interface BusinessProfile {
  id: string;
  ownerId: string;
  registrationId: string;
  /** The business's own branding. Independent of the owner's personal name, and not unique. */
  businessName: string;
  /** Lowercased businessName, stored so search can prefix-match without a full scan. */
  businessNameLower?: string;
  description: string | null;
  shopLink: string;
  /** Exactly PORTFOLIO_IMAGE_COUNT Storage URLs. Raw images: no captions, no prices. */
  portfolioImages: string[];
  status: BusinessProfileStatus;
  suspendedAt?: any;
  suspendedBy?: string | null;
  suspendReason?: string | null;
  /** Per-profile, not account-wide: three businesses can be linked or hidden independently. */
  showOnPersonalProfile: boolean;
  createdAt?: any;
  lastEditedAt?: any;
  pendingEdit?: PendingBusinessEdit | null;
  /**
   * Mirrors `pendingEdit != null` as a plain boolean so the admin queue can
   * query it. Firestore's `!=` distinguishes an absent field from a null one, so
   * a query on pendingEdit itself would silently miss rows.
   */
  hasPendingEdit?: boolean;
  /** Denormalized so the store-cap rule can check a counter instead of counting docs. */
  storeItemCount: number;
  // Basic analytics. Denormalized counters, not an analytics subsystem.
  viewCount: number;
  storeTapCount: number;
  confirmedPurchaseCount: number;
}

// ── Feed (off-timeline) ──────────────────────────────────────────────────────

export type BusinessFeedMediaType = 'image' | 'video' | 'text';

/**
 * A post on a business profile. Deliberately has no likes or comments: this is
 * a broadcast surface, and the groundwork for ads rather than a second timeline.
 * Nothing injects these into /yaps.
 */
export interface BusinessFeedPost {
  id: string;
  authorId: string;
  text: string;
  mediaUrls: string[];
  mediaType: BusinessFeedMediaType;
  createdAt?: any;
}

// ── Store ────────────────────────────────────────────────────────────────────

/**
 * price is a string on purpose. It is displayed, never arithmetic'd — the real
 * transaction happens on the business's own site, so storing "£25" or "From $40"
 * verbatim is more honest than a number plus a currency the app would have to
 * guess at.
 */
export interface BusinessStoreItem {
  id: string;
  title: string;
  description: string;
  price: string;
  imageUrl: string;
  purchaseLink: string;
  createdAt?: any;
}

// ── Purchase tracking ────────────────────────────────────────────────────────

export type PurchaseCheckStatus = 'pending' | 'confirmed' | 'declined';

export interface PurchaseCheck {
  id: string;
  /** Always set: logged-out visitors tap through without a check doc at all. */
  userId: string;
  businessProfileId: string;
  businessOwnerId: string;
  productId: string;
  /** Denormalized so the owner's notification names the product without a lookup. */
  productTitle: string;
  /** Short random code appended to purchaseLink so the business can match the visit. */
  trackingCode: string;
  tappedAt?: any;
  status: PurchaseCheckStatus;
  respondedAt?: any;
  /** Set by the trigger once the owner notification actually fires. Null means suppressed. */
  notifiedAt?: any;
}

// ── Per-owner limits ─────────────────────────────────────────────────────────

export interface BusinessOwnerState {
  ownerId: string;
  /** Null / absent once served. Set by a denial or a self-deletion. */
  cooldownExpiresAt?: any;
  reason?: 'denied' | 'self_deleted';
  /**
   * Profiles this account currently owns, maintained by the
   * onBusinessProfileCreated / onBusinessProfileDeleted triggers. The create
   * rule reads it, so it has to be written somewhere the owner cannot reach.
   */
  activeProfileCount?: number;
  createdAt?: any;
  updatedAt?: any;
}

// ── Notification types ───────────────────────────────────────────────────────

export const BUSINESS_NOTIFICATION_TYPES = {
  APPROVED: 'BUSINESS_APPROVED',
  DENIED: 'BUSINESS_DENIED',
  SUSPENDED: 'BUSINESS_SUSPENDED',
  PURCHASE: 'BUSINESS_PURCHASE',
  EDIT_APPROVED: 'BUSINESS_EDIT_APPROVED',
  EDIT_DENIED: 'BUSINESS_EDIT_DENIED',
} as const;

// ── Shared helpers ───────────────────────────────────────────────────────────

/** Firestore Timestamp | Date | ISO string → millis, or null when unreadable. */
export function toMillis(value: any): number | null {
  if (value == null) return null;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/**
 * Normalize whatever the owner typed into something openable.
 * A bare "shop.example.com" is what people actually type; without a scheme the
 * browser treats it as a relative path and the tap lands inside the app.
 */
export function normalizeShopLink(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** True when the string is a plausible http(s) URL with a dotted host. */
export function isValidShopLink(raw: string): boolean {
  const candidate = normalizeShopLink(raw);
  if (!candidate) return false;
  try {
    const url = new URL(candidate);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
  } catch {
    return false;
  }
}

/**
 * Append the tracking code without clobbering whatever query the owner's link
 * already carries, and without disturbing a fragment.
 */
export function appendTrackingCode(purchaseLink: string, trackingCode: string): string {
  const base = normalizeShopLink(purchaseLink);
  if (!base) return base;
  try {
    const url = new URL(base);
    url.searchParams.set(TRACKING_PARAM, trackingCode);
    return url.toString();
  } catch {
    // Unparseable link: fall back to naive concatenation rather than dropping
    // the code, since the code is the only thing tying a visit to a check.
    const joiner = base.includes('?') ? '&' : '?';
    return `${base}${joiner}${TRACKING_PARAM}=${encodeURIComponent(trackingCode)}`;
  }
}

/**
 * "women 25-40, natural hair, uk" → ["women 25-40", "natural hair", "uk"]
 *
 * Trims, drops blanks, de-duplicates case-insensitively while keeping the
 * applicant's own capitalisation, and caps both the count and each keyword's
 * length. Splits on commas only: spaces are legitimate inside a keyword.
 */
export function parseCustomerBase(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of (raw || '').split(',')) {
    const keyword = part.trim().slice(0, CUSTOMER_BASE_KEYWORD_MAX_LENGTH);
    if (!keyword) continue;
    const key = keyword.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(keyword);
    if (out.length >= CUSTOMER_BASE_MAX_KEYWORDS) break;
  }
  return out;
}

/** Human-readable countdown for a cooldown, e.g. "6 days". */
export function describeCooldown(expiresAt: any): string | null {
  const ms = toMillis(expiresAt);
  if (ms == null || ms <= Date.now()) return null;
  const days = Math.ceil((ms - Date.now()) / (24 * 60 * 60 * 1000));
  if (days <= 1) {
    const hours = Math.ceil((ms - Date.now()) / (60 * 60 * 1000));
    return hours <= 1 ? 'less than an hour' : `${hours} hours`;
  }
  return `${days} days`;
}
